import "@/App.css";
import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Layout } from "@/components/Layout";

const importHome = () => import("@/pages/Home");
const importServices = () => import("@/pages/Services");
const importWork = () => import("@/pages/Work");
const importAbout = () => import("@/pages/About");
const importContact = () => import("@/pages/Contact");
const importInsights = () => import("@/pages/Insights");
const importInsightArticle = () => import("@/pages/InsightArticle");
const importAwards = () => import("@/pages/Awards");
const importPrivacy = () => import("@/pages/Privacy");
const importAdmin = () => import("@/pages/Admin");
const importNotFound = () => import("@/pages/NotFound");
const importDiagnostic = () => import("@/pages/Diagnostic");
const importReview = () => import("@/pages/Review");

const Home = lazy(importHome);
const Services = lazy(importServices);
const Work = lazy(importWork);
const About = lazy(importAbout);
const Contact = lazy(importContact);
const Insights = lazy(importInsights);
const InsightArticle = lazy(importInsightArticle);
const Awards = lazy(importAwards);
const Privacy = lazy(importPrivacy);
const Admin = lazy(importAdmin);
const NotFound = lazy(importNotFound);
const Diagnostic = lazy(importDiagnostic);
const Review = lazy(importReview);

const RouteFallback = () => (
  <div className="min-h-screen bg-[#F8F9FA]" aria-hidden="true" />
);

// Warm all route chunks in the background so in-app navigation is instant
// (no per-route "loading" flash) while keeping a small initial bundle.
const prefetchRoutes = () => {
  [
    importServices, importWork, importAbout, importContact,
    importInsights, importInsightArticle, importAwards, importPrivacy,
  ].forEach((fn) => fn());
};

function App() {
  useEffect(() => {
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 300));
    const id = idle(prefetchRoutes);
    return () => {
      if (window.cancelIdleCallback && typeof id === "number") window.cancelIdleCallback(id);
    };
  }, []);

  return (
    <BrowserRouter>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/work" element={<Work />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/insights/:slug" element={<InsightArticle />} />
            <Route path="/awards" element={<Awards />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/reporting-consistency-diagnostic" element={<Diagnostic />} />
            <Route path="/stakeholder-communications-review" element={<Review />} />
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route path="/admin" element={<Admin />} />
        </Routes>
      </Suspense>
      <Toaster position="bottom-right" richColors theme="light" />
    </BrowserRouter>
  );
}

export default App;
